import { useState, useEffect } from 'react';
import { useSupabase } from '../../../providers/SupabaseProvider';
import { useAuth } from '../../../providers/SupabaseProvider';
import { useSampleActions } from '../../../utils/sampleActions';
import { annotateVacationConflicts, VacationConflict } from '../../../utils/vacationConflicts';

const SAMPLE_PAGE_SIZE = 40;
const MAIN_LOCATION_FILTER = '__main_location__';

interface OrganizationLocationOption {
  id: string;
  name: string;
}

const STATUS_PRIORITY: Record<string, number> = {
  'appointed': 1,
  'scheduled': 2,
  'collected': 3,
  'sent': 4,
  'received': 5,
  'completed': 6,
  'rejected': 7,
};

export function useSampleData() {
  const supabase = useSupabase();
  const { user } = useAuth();
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showNewSampleModal, setShowNewSampleModal] = useState(false);
  const [showRequisitionModal, setShowRequisitionModal] = useState(false);
  const [requisitionSample, setRequisitionSample] = useState<any>(null);
  const [hardDeleting, setHardDeleting] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Steuert, ob die Standort-Filterung laut Organisations-Feature verfügbar ist.
  const [hasLocationFeature, setHasLocationFeature] = useState(false);
  // Hält alle Standorte der Organisation für die Auswahl im Filter bereit.
  const [organizationLocations, setOrganizationLocations] = useState<OrganizationLocationOption[]>([]);
  // Speichert den gewählten Standortfilter (leer = alle, `MAIN_LOCATION_FILTER` = Kunden ohne Standort).
  const [selectedLocationFilter, setSelectedLocationFilter] = useState('');

  // Get organization ID
  useEffect(() => {
    async function getOrganizationId() {
      if (!user) return;
      
      try {
        const { data: orgData, error: orgUserError } = await supabase
          .from('organization_users')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();
          
        if (orgUserError) throw orgUserError;
        if (orgData) setOrganizationId(orgData.organization_id);
      } catch (err) {
        console.error('Error getting organization ID:', err);
      }
    }

    getOrganizationId();
  }, [user]);

  useEffect(() => {
    async function loadLocationFeatureAndLocations() {
      if (!organizationId) {
        setHasLocationFeature(false);
        setOrganizationLocations([]);
        setSelectedLocationFilter('');
        return;
      }

      try {
        // Prüft, ob das Standort-Feature aktiv ist, damit der Filter nur dann angezeigt wird.
        const { data: featureData, error: featureError } = await supabase
          .from('organization_features')
          .select('feature_locations')
          .eq('organization_id', organizationId)
          .maybeSingle();

        if (featureError) throw featureError;

        const locationFeatureEnabled = Boolean(featureData?.feature_locations);
        setHasLocationFeature(locationFeatureEnabled);

        if (!locationFeatureEnabled) {
          setOrganizationLocations([]);
          setSelectedLocationFilter('');
          return;
        }

        // Lädt verfügbare Standorte zur Auswahl des Proben-Filters.
        const { data: locationsData, error: locationsError } = await supabase
          .from('organization_locations')
          .select('id, name')
          .eq('organization_id', organizationId)
          .order('name', { ascending: true });

        if (locationsError) throw locationsError;

        setOrganizationLocations((locationsData || []) as OrganizationLocationOption[]);
      } catch (locationConfigError) {
        console.error('Error loading location feature configuration:', locationConfigError);
        setHasLocationFeature(false);
        setOrganizationLocations([]);
        setSelectedLocationFilter('');
      }
    }

    loadLocationFeatureAndLocations();
  }, [organizationId, supabase]);

  const sortSamplesByPriority = (list: any[]) => {
    return [...list].sort((a, b) => {
      const statusA = STATUS_PRIORITY[a.status] ?? 999;
      const statusB = STATUS_PRIORITY[b.status] ?? 999;

      if (statusA !== statusB) {
        return statusA - statusB;
      }

      const dateA = a.scheduled_date ? new Date(a.scheduled_date).getTime() : 0;
      const dateB = b.scheduled_date ? new Date(b.scheduled_date).getTime() : 0;

      return dateA - dateB;
    });
  };

  const customLoadSamples = async () => {
    if (!organizationId) return;

    setHasMore(true);
    setPage(0);
    await loadSamplesPage(0, true);
  };

  const {
    loading, setLoading,
    error, setError,
    samples, setSamples,
    sampleSubstances, setSampleSubstances,
    expandedSample, setExpandedSample,
    processingId, setProcessingId,
    showCollectionModal, setShowCollectionModal,
    showAppointmentModal, setShowAppointmentModal,
    showSendModal, setShowSendModal,
    showReceiveResultModal, setShowReceiveResultModal,
    selectedSample, setSelectedSample,
    selectedSampleId, setSelectedSampleId,
    handleScheduleAppointment,
    handleCollectSample,
    handleSendSample,
    handleReceiveResult,
    handleDeleteSample,
    handleNoShow,
    handleResetSample,
    loadSampleSubstances
  } = useSampleActions(supabase, null, customLoadSamples);

  async function loadSamplesPage(pageToLoad: number, reset: boolean) {
    if (!organizationId) return;

    if (reset) {
      setLoading(true);
      setSamples([]);
      setSampleSubstances({});
      setExpandedSample(null);
    } else {
      setIsLoadingMore(true);
    }

    setError(null);

    try {
      const from = pageToLoad * SAMPLE_PAGE_SIZE;
      const to = from + SAMPLE_PAGE_SIZE - 1;

      let customerIdFilter: string[] | null = null;

      if (hasLocationFeature && selectedLocationFilter) {
        let customerLocationQuery = supabase
          .from('customers')
          .select('id')
          .eq('organization_id', organizationId);

        // Der Hauptstandort entspricht Kunden ohne explizite Standort-Zuordnung.
        if (selectedLocationFilter === MAIN_LOCATION_FILTER) {
          customerLocationQuery = customerLocationQuery.is('location_id', null);
        } else {
          customerLocationQuery = customerLocationQuery.eq('location_id', selectedLocationFilter);
        }

        const { data: filteredCustomers, error: filteredCustomersError } = await customerLocationQuery;

        if (filteredCustomersError) throw filteredCustomersError;

        customerIdFilter = (filteredCustomers || []).map((customer) => customer.id);

        if (customerIdFilter.length === 0) {
          setSamples([]);
          setHasMore(false);
          setPage(pageToLoad);
          return;
        }
      }

      let sampleQuery = supabase
        .from('sample_details')
        .select('*', { count: 'exact' })
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
        .range(from, to);

      // Wendet den Standort-basierten Kundenfilter auf die Probenliste an.
      if (customerIdFilter) {
        sampleQuery = sampleQuery.in('customer_id', customerIdFilter);
      }

      const { data: samplesData, error: samplesError, count } = await sampleQuery;

      if (samplesError) throw samplesError;

      const sortedPage = sortSamplesByPriority(samplesData || []);

      let annotatedSamples = sortedPage.map((sample) => ({
        ...sample,
        vacation_conflicts: [] as VacationConflict[],
        assigned_user_name: null as string | null,
      }));

      try {
        const customerIds = Array.from(
          new Set(
            sortedPage
              .map((sample) => sample.customer_id)
              .filter((id): id is string => Boolean(id))
          )
        );

        if (customerIds.length > 0) {
          const chunkSize = 100;
          let vacations: VacationConflict[] = [];

          for (let i = 0; i < customerIds.length; i += chunkSize) {
            const chunk = customerIds.slice(i, i + chunkSize);
            const { data: vacationData, error: vacationError } = await supabase
              .from('vacation_requests')
              .select('id, customer_id, contract_id, start_date, end_date, status, reason')
              .eq('organization_id', organizationId)
              .in('status', ['approved', 'pending', 'planned', 'submitted'])
              .in('customer_id', chunk);

            if (vacationError) throw vacationError;

            vacations = vacations.concat((vacationData || []) as VacationConflict[]);
          }

          if (vacations.length > 0) {
            annotatedSamples = annotateVacationConflicts(sortedPage, vacations);
          }
        }
      } catch (vacationError) {
        console.error('Error loading vacation conflicts:', vacationError);
      }

      const assignedUserIds = Array.from(
        new Set(
          annotatedSamples
            .map((sample) => sample.assigned_user_id)
            .filter((id): id is string => typeof id === 'string' && id.length > 0)
        )
      );

      if (assignedUserIds.length > 0) {
        try {
          const { data: assignedMembers, error: assignedMembersError } = await supabase
            .from('organization_members')
            .select('organization_id, user_id, full_name, email')
            .eq('organization_id', organizationId)
            .in('user_id', assignedUserIds);

          if (assignedMembersError) throw assignedMembersError;

          const assignedNameByUserId = new Map<string, string>();
          (assignedMembers || []).forEach((member) => {
            assignedNameByUserId.set(member.user_id, member.full_name?.trim() || member.email || member.user_id);
          });

          annotatedSamples = annotatedSamples.map((sample) => {
            if (!sample.assigned_user_id) return sample;
            return {
              ...sample,
              assigned_user_name: assignedNameByUserId.get(sample.assigned_user_id) || sample.assigned_user_id,
            };
          });
        } catch (assignedLookupError) {
          console.error('Error loading assigned sample users:', assignedLookupError);
        }
      }
      setSamples((prevSamples) => {
        const base = reset ? [] : prevSamples;
        const mergedMap = new Map(base.map((sample: any) => [sample.id, sample]));

        annotatedSamples.forEach((sample) => {
          mergedMap.set(sample.id, sample);
        });

        return sortSamplesByPriority(Array.from(mergedMap.values()));
      });

      if (annotatedSamples.length > 0) {
        await loadSampleSubstances(annotatedSamples.map((s: any) => s.id));
      }

      const fetchedCount = samplesData?.length ?? 0;
      if (typeof count === 'number') {
        const totalLoaded = Math.min(count, from + fetchedCount);
        setHasMore(totalLoaded < count);
      } else {
        setHasMore(fetchedCount === SAMPLE_PAGE_SIZE);
      }

      setPage(pageToLoad);
    } catch (err) {
      console.error('Error loading samples:', err);
      setError('Fehler beim Laden der Proben');
      if (!reset) {
        setHasMore(false);
      }
    } finally {
      if (reset) {
        setLoading(false);
      }
      setIsLoadingMore(false);
    }
  }

  useEffect(() => {
    if (organizationId) {
      customLoadSamples();
    }
  }, [organizationId, refreshTrigger, hasLocationFeature, selectedLocationFilter]);

  const loadMoreSamples = async () => {
    if (!organizationId || isLoadingMore || loading || !hasMore) {
      return;
    }

    const nextPage = page + 1;
    await loadSamplesPage(nextPage, false);
  };

  // Function to handle editing a sample
  // Function to handle hard deleting a sample
  const handleHardDeleteSample = async (sampleId: string) => {
    if (!confirm("Möchten Sie diese Probe ENDGÜLTIG löschen? Diese Aktion kann NICHT rückgängig gemacht werden!")) return;

    setHardDeleting(true);
    setProcessingId(sampleId);
    setError(null);
    
    try {
      // First delete all substances associated with this sample
      const { error: substancesError } = await supabase
        .from('customer_sample_substances')
        .delete()
        .eq('sample_id', sampleId);
        
      if (substancesError) throw substancesError;
      
      // For contract samples, update the contract_samples table to remove the link
      const { data: contractSample, error: contractSampleError } = await supabase
        .from('contract_samples')
        .select('id')
        .eq('sample_id', sampleId)
        .maybeSingle();

      if (!contractSampleError && contractSample) {
        // Update the contract sample to remove the link to the customer sample
        const { error: updateError } = await supabase
          .from('contract_samples')
          .update({ sample_id: null })
          .eq('id', contractSample.id);

        if (updateError) throw updateError;
      }
      
      // Delete any documents associated with this sample
      const { error: documentsError } = await supabase
        .from('customer_documents')
        .update({ deleted_at: new Date().toISOString() })
        .eq('sample_id', sampleId);
        
      if (documentsError) throw documentsError;

      // Finally, delete the sample itself
      const { error } = await supabase
        .from('customer_samples')
        .delete()
        .eq('id', sampleId);

      if (error) throw error;

      // Reload samples after deletion
      await customLoadSamples();
    } catch (err) {
      console.error("Error hard deleting sample:", err);
      setError("Fehler beim endgültigen Löschen der Probe");
    } finally {
      setProcessingId(null);
      setHardDeleting(false);
    }
  };
  
  // Handler for creating requisition form
  const handleCreateRequisition = (sample: any) => {
    setRequisitionSample(sample);
    setShowRequisitionModal(true);
  };

  return {
    loading,
    error,
    samples,
    sampleSubstances,
    hasMore,
    isLoadingMore,
    hasLocationFeature,
    organizationLocations,
    selectedLocationFilter,
    mainLocationFilterValue: MAIN_LOCATION_FILTER,
    processingId,
    showNewSampleModal,
    showCollectionModal,
    showAppointmentModal,
    showSendModal,
    showReceiveResultModal,
    showRequisitionModal,
    selectedSample,
    selectedSampleId,
    expandedSample,
    requisitionSample,
    setShowNewSampleModal,
    setProcessingId,
    setShowCollectionModal,
    setShowAppointmentModal,
    setSelectedSample,
    setShowSendModal,
    setSelectedSampleId,
    setShowReceiveResultModal,
    setShowRequisitionModal,
    setRequisitionSample,
    setExpandedSample,
    setSelectedLocationFilter,
    handleScheduleAppointment,
    handleCollectSample,
    handleSendSample,
    handleReceiveResult,
    handleDeleteSample,
    handleNoShow,
    handleResetSample,
    handleHardDeleteSample,
    handleCreateRequisition,
    loadSamples: customLoadSamples,
    loadMoreSamples,
    refreshTrigger,
    setRefreshTrigger,
    organizationId,
    hardDeleting
  };
}

// Export as default instead of named export
export default useSampleData;

